import { useAuthStore } from '@/stores/auth-store'
import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'

import AppLayout from '../layouts/AppLayout.vue'
import Callback from '../pages/auth/Callback.vue'
import { usePermissionStore } from '@/stores/permission-store'
import { OrganizationType } from '@/enums'
import { useConsentStore } from '@/stores/consent-store'
import { useEnvironmentStore } from '@/stores/environment-store'
import { setLanguageAndLoad } from '@/i18n/i18n'
import { useGlobalStore } from '@/stores/global-store'

export const routes: Array<RouteRecordRaw> = [
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'dashboard' },
  },
  {
    name: 'admin',
    path: '/',
    component: AppLayout,
    redirect: { name: 'dashboard' },
    meta: {
      acceptConsent: true,
    },
    children: [
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
        meta: {
          auth: true,
        },
      },
      {
        name: 'whoami',
        path: '/whoami',
        component: () => import('../pages/admin/pages/Whoami.vue'),
      },
      {
        name: 'backoffice-profile',
        path: 'backoffice-profile',
        component: () => import('@/pages/backoffice/backoffice-profile/BackofficeProfilePage.vue'),
        meta: {
          auth: true,
          requiredPermissions: ['backoffices::view'],
        },
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/settings/Settings.vue'),
        meta: {
          auth: true,
        },
      },
      {
        name: 'clients',
        path: 'clients',
        component: () => import('../pages/patients/PatientsPage.vue'),
        meta: {
          auth: true,
          requiredPermissions: ['menu::clients'],
        },
      },
      {
        name: 'coach-profile',
        path: 'coach-profile',
        component: () => import('@/pages/coach/coach-profile/CoachProfilePage.vue'),
        meta: {
          auth: true,
          requiredPermissions: ['menu::coach-profile'],
        },
      },
      {
        name: 'browse',
        path: 'browse',
        component: () => import('../pages/client/browse/BrowsePage.vue'),
        meta: {
          auth: true,
          requiredPermissions: ['menu::explore'],
        },
      },
      {
        name: 'resubmit-plan',
        path: 'clients/:clientId/resubmit-plan/:planId',
        component: () => import('../pages/plan-create/Resubmit/ResubmitPlanPage.vue'),
        meta: {
          auth: true,
          requiredPermissions: ['menu::plan_verification'],
        },
      },
      {
        name: 'edit-client',
        path: 'clients/edit/:clientId',
        component: () => import('../pages/patient-edit/EditPatient/EditPatientPage.vue'),
        meta: {
          auth: true,
          requiredPermissions: ['patients::create'],
        },
      },
      {
        name: 'coach-info',
        path: 'coach-info',
        component: () => import('../pages/client/coach-info/CoachInfo.vue'),
        meta: {
          auth: true,
          requiredPermissions: ['menu::coach_info'],
        },
      },
      {
        name: 'executive-list',
        path: 'executive-list',
        component: () => import('../pages/executive-list/ExecutiveListPage.vue'),
        meta: {
          auth: true,
          requiredPermissions: ['menu::executives'],
        },
      },
      {
        name: 'coaches',
        path: 'coaches',
        component: () => import('../pages/coaches/CoachesPage.vue'),
        meta: {
          auth: true,
          requiredPermissions: ['menu::coaches'],
        },
      },
      {
        name: 'edit-coach',
        path: 'coaches/edit/:coachId',
        component: () => import('@/pages/coach/coach-edit/EditCoachPage.vue'),
        meta: {
          auth: true,
          requiredPermissions: ['coaches::update'],
        },
      },
      {
        name: 'create-coach',
        path: 'coaches/create/:NLHId',
        component: () => import('@/pages/coach/coach-create/CreateCoachPage.vue'),
        meta: {
          auth: true,
          requiredPermissions: ['coaches::create'],
        },
      },
      {
        name: 'create-executive',
        path: 'executive-list/create-executive/:NLHId',
        component: () => import('@/pages/executive/executive-create/CreateExecutivePage.vue'),
        meta: {
          auth: true,
          requiredPermissions: ['executives::create'],
        },
      },
      {
        name: 'edit-executive',
        path: 'executive-list/edit-executive/:executiveId',
        component: () => import('@/pages/executive/executive-edit/EditExecutivePage.vue'),
        meta: {
          auth: true,
          requiredPermissions: ['executives::update'],
        },
      },
      {
        name: 'client-progress',
        path: 'client-progress',
        component: () => import('../pages/client/progress/ClientProgress.vue'),
        meta: {
          auth: true,
          requiredPermissions: ['menu::progress'],
        },
      },
      {
        name: 'plans-verification',
        path: 'plans-verification',
        component: () => import('@/pages/plans-verification/PlansVerificationPage.vue'),
        meta: {
          auth: true,
          requiredPermissions: ['menu::plan_verification'],
        },
      },
      {
        name: 'food-converter',
        path: 'food-converter',
        component: () => import('@/pages/food-converter/FoodConverterPage.vue'),
        meta: {
          auth: true,
          requiredPermissions: ['menu::food_converter'],
        },
      },
      {
        name: 'nlh-list',
        path: 'nlh-list',
        component: () => import('@/pages/nlh-list/NLHListPage.vue'),
        meta: {
          auth: true,
          requiredPermissions: ['menu::nlhs'],
        },
      },
      {
        name: 'edit-nlh',
        path: 'nlh-list/edit/:NLHId',
        component: () => import('@/pages/nlh/nlh-edit/EditNLHPage.vue'),
        meta: {
          auth: true,
          requiredPermissions: ['nlh::update'],
        },
      },
      {
        name: 'create-nlh',
        path: 'nlh/create',
        component: () => import('@/pages/nlh/nlh-create/CreateNLHPage.vue'),
        meta: {
          auth: true,
          requiredPermissions: ['nlh::create'],
        },
      },
      {
        name: 'nlh-profile',
        path: 'nlh-profile',
        component: () => import('@/pages/nlh/nlh-profile/NLHProfilePage.vue'),
        meta: {
          auth: true,
          requiredPermissions: ['menu::nlh-profile'],
        },
      },
      {
        name: 'executive-profile',
        path: 'executive-profile',
        component: () => import('@/pages/executive/executive-profile/ExecutiveProfilePage.vue'),
        meta: {
          auth: true,
          requiredPermissions: ['menu::executive-profile'],
        },
      },
      {
        name: 'market-list',
        path: 'markets',
        component: () => import('@/pages/market-list/MarketListPage.vue'),
        meta: {
          auth: true,
          requiredPermissions: ['menu::markets'],
        },
      },
      {
        name: 'laboratories',
        path: 'laboratories',
        component: () => import('../pages/laboratories/LaboratoriesPage.vue'),
        meta: {
          auth: true,
          requiredPermissions: ['menu::laboratories'],
        },
      },
      {
        name: 'edit-laboratory',
        path: 'laboratories/edit/:laboratoryId',
        component: () => import('../pages/laboratory-edit/EditLaboratoryPage.vue'),
        meta: {
          auth: true,
          requiredPermissions: ['laboratory::update'],
        },
      },
      {
        name: 'laboratories-admin',
        path: 'laboratories-admin',
        component: () => import('../pages/laboratories-admin/LaboratoriesAdminPage.vue'),
        meta: {
          auth: true,
          requiredPermissions: ['menu::laboratories-admin'],
        },
      },
      {
        name: 'edit-laboratory-admin',
        path: 'laboratories-admin/edit/:laboratoryId',
        component: () => import('../pages/laboratory-edit/EditLaboratoryPage.vue'),
        meta: {
          auth: true,
          requiredPermissions: ['laboratory::update'],
        },
      },
      {
        name: 'clients-list',
        path: 'clients-list',
        component: () => import('@/pages/clients-list/ClientsListPage.vue'),
        meta: {
          auth: true,
          requiredPermissions: ['menu::clients-list'],
        },
      },
      {
        name: 'NLH-commercials',
        path: '/NLH-commercials',
        component: () => import('@/pages/commercials/nlh-commercials/NLHCommercialsPage.vue'),
      },
      {
        name: 'coach-commercials',
        path: '/coach-commercials',
        component: () => import('@/pages/commercials/coach-commercials/CoachCommercialsPage.vue'),
      },
      {
        name: 'StripePaymentSuccess',
        path: '/stripe-payment-success',
        component: () => import('../pages/payment/stripe/StripePaymentSuccess.vue'),
        meta: {
          auth: true,
          requiredPermissions: ['payments::complete'],
        },
      },
      {
        name: 'statistics',
        path: 'statistics',
        component: () => import('@/pages/statistics/StatisticsPage.vue'),
        meta: {
          auth: true,
          requiredPermissions: ['reports::statistics::view'],
        },
      },
      {
        name: 'invoicing',
        path: 'invoicing',
        component: () => import('@/pages/invoicing/InvoicingPage.vue'),
        meta: {
          auth: true,
          requiredPermissions: ['invoices::list'],
        },
      },
      {
        name: 'translations',
        path: 'translations',
        component: () => import('../pages/translations/TranslationsListPage.vue'),
        meta: {
          auth: true,
          requiredPermissions: ['menu::translations'],
        },
      },
      {
        name: 'PaypalPaymentSuccess',
        path: '/paypal-payment-success',
        component: () => import('../pages/payment/paypal/PaypalPaymentSuccess.vue'),
        meta: {
          auth: true,
          requiredPermissions: ['payments::complete'],
        },
      },
      {
        name: 'RevolutPaymentSuccess',
        path: '/revolut-payment-success',
        component: () => import('../pages/payment/revolut/RevolutPaymentSuccess.vue'),
        meta: {
          auth: true,
          requiredPermissions: ['payments::complete'],
        },
      },
    ],
  },
  {
    path: '/callback',
    name: 'Autorization-Callback',
    component: Callback,
    meta: {
      auth: false,
    },
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
  {
    name: 'coach-consent',
    path: '/coach-consent',
    component: () => import('../pages/consent/CoachConsent.vue'),
  },
  {
    name: 'executive-consent',
    path: '/executive-consent',
    component: () => import('../pages/consent/ExecutiveConsent.vue'),
  },
  {
    name: 'nlh-consent',
    path: '/nlh-consent',
    component: () => import('../pages/consent/NLHConsent.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  linkActiveClass: 'active',
  linkExactActiveClass: 'active',
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const permissionStore = usePermissionStore()
  const consentStore = useConsentStore()
  const envStore = useEnvironmentStore()
  const globalStore = useGlobalStore()

  // Waiting for session validation
  await authStore.checkUserAuthenticated()

  envStore.setEnvironment()

  // If the route requires authentication
  if (to.meta.auth) {
    if (!authStore.isAuthenticated) {
      await authStore.login() // Redirecting to an external Identity Server
      return // Must exit from beforeEach
    }
  }

  if (authStore.token && !authStore.accountInfo) {
    try {
      await authStore.loadAccountInfo()
    } catch (error) {
      console.error('Error loading account info:', error)
    }
  }

  if (authStore.token) {
    await globalStore.loadLanguage()
    await setLanguageAndLoad(globalStore.language)
  }

  // If accept policy required
  if (to.meta.acceptConsent && !authStore.isInactive && !consentStore.getConsentLater) {
    if (
      consentStore.getRequiredConsents == null ||
      (consentStore.getRequiredConsents && consentStore.getRequiredConsents.length)
    ) {
      switch (authStore.getAccountOrganizationType) {
        case OrganizationType.Coach:
          next('/coach-consent')
          break
        case OrganizationType.Executive:
          next('/executive-consent')
          break
        case OrganizationType.NLH:
          next('/nlh-consent')
          break
      }
      return
    }
  }

  // If specific permissions are required
  if (to.meta.requiredPermissions) {
    const requiredPermissions = to.meta.requiredPermissions as string[]
    const results = await Promise.all(requiredPermissions.map((p) => permissionStore.hasPermission(p)))
    const hasAllPermissions = results.every(Boolean)

    if (!hasAllPermissions) {
      alert('Access denied!')
      next('/')
      return
    }
  }

  // If everything is okay — proceed further
  next()
})

router.onError((error) => {
  console.log('error', error)
  if (error?.message?.includes('Failed to fetch dynamically')) {
    console.log('reload')
    window.location.reload()
  }
})

export default router
